'use client';

import { useEffect, useState } from 'react';
import {
  BlockRoot,
  PortalLink,
  tx,
  useCart,
  useCommerceSource,
  type ProductCard,
} from '@akademi/site-builder-runtime';
import { useSolarText } from '../locales/useSolarText';
import { LivePrice } from '../home/parts';
import { totalOf, type LiveMoney } from '../home/money';
import { TileGlyph, TILE_ICONS, type TileIcon } from '../home/sceneArt';
import { TradeStyles } from '../styles/TradeStyles';
import { HOME_CSS } from '../styles/homeCss';
import { availabilityLabel } from '../home/availability';

type Localized = string | Record<string, string>;

export interface SolarProductRailProps {
  title?: Localized;
  /** The category the row is drawn from, as its slug path (`panels`, `pv-inverters`). */
  categorySlugPath?: string;
  /** How many products. Default 6 — one row at the shop's full width. */
  limit?: number;
  seeAllLabel?: Localized;
  seeAllHref?: string;
  /** Drawn where a product has no photograph yet: one of the theme's `TILE_ICONS`. */
  glyph?: TileIcon;
}

/**
 * One row of live products on the home page: "Panels", "Inverters" — Horizon's card anatomy
 * (`solar/docs/design/redesign-2026-10-01/`, `index.html` `.card`): media stage over a
 * hairline, brand eyebrow, title, the price foot ruled off, and the owner-chosen variant-C
 * add button (navy, one-line "Add" with the basket glyph, 2026-10-01). Pence-per-watt and
 * the card datasheet links stay off until the listing API returns attributes and document
 * links (follow-up F4 in the go-live plan).
 *
 * <p>Not the platform's `ProductGrid`, which is a listing — it pages, and on a home page its
 * pager writes `?page=` into the home URL. A row is a shop window: a few products, their live
 * price on both VAT bases, stock, add to basket, and a way to the whole category.
 *
 * <p>An empty or unreachable category renders nothing: a heading over no products on a home
 * page reads as "we have none".
 */
export function SolarProductRail({
  title,
  categorySlugPath,
  limit = 6,
  seeAllLabel,
  seeAllHref,
  glyph = 'panel',
}: SolarProductRailProps) {
  const commerce = useCommerceSource();
  const [items, setItems] = useState<ProductCard[] | null>(null);

  useEffect(() => {
    if (!commerce) return;
    let cancelled = false;
    commerce
      .listProducts({ categorySlugPath: categorySlugPath || undefined, size: limit, inStockOnly: true })
      .then((listing) => !cancelled && setItems(listing.items.slice(0, limit)))
      .catch(() => !cancelled && setItems([]));
    return () => {
      cancelled = true;
    };
  }, [commerce, categorySlugPath, limit]);

  if (!commerce || (items && items.length === 0)) return null;

  return (
    <BlockRoot as="section" className="ae ae-wrap ae-sec">
      <TradeStyles id="home" css={HOME_CSS} />
      <div className="ae-sec__head">
        {tx(title) ? <h2 className="ae-sec__title">{tx(title)}</h2> : <span />}
        {seeAllHref && tx(seeAllLabel) ? (
          <PortalLink href={seeAllHref} className="ae-sec__more">
            {tx(seeAllLabel)} →
          </PortalLink>
        ) : null}
      </div>
      <ul className="ae-rail">
        {(items ?? Array.from({ length: limit }, () => null)).map((item, index) => (
          <li key={item?.id ?? `loading-${index}`}>
            {item ? <SolarProductCard item={item} glyph={glyph} /> : <div className="ae-card ae-card--loading" aria-hidden="true" />}
          </li>
        ))}
      </ul>
    </BlockRoot>
  );
}

export function SolarProductCard({ item, glyph }: { item: ProductCard; glyph: TileIcon }) {
  const t = useSolarText();
  const cart = useCart();
  const [state, setState] = useState<'idle' | 'busy' | 'added' | 'failed'>('idle');
  const total = totalOf([{ price: item.price as LiveMoney | undefined, quantity: 1 }]);
  const outOfStock = item.availability === 'OUT_OF_STOCK';

  async function add() {
    if (!cart?.available || state === 'busy') return;
    setState('busy');
    try {
      setState((await cart.add({ productId: item.id, quantity: '1' })) ? 'added' : 'failed');
    } catch {
      setState('failed');
    }
  }

  return (
    <article className="ae-card">
      <PortalLink href={item.publicPath} className="ae-card__link">
        <span className="ae-card__media">
          {item.heroImage?.url ? (
            <img src={item.heroImage.url} alt={item.heroImage.alt || item.title} loading="lazy" />
          ) : (
            <TileGlyph icon={TILE_ICONS.includes(glyph) ? glyph : 'panel'} />
          )}
        </span>
        <span className="ae-card__text">
          {item.brandName ? <span className="ae-card__brand">{item.brandName}</span> : null}
          <span className="ae-card__title">{item.title}</span>
        </span>
      </PortalLink>
      <div className="ae-card__foot">
        <LivePrice loading={false} total={total} />
        <span className="ae-card__stock" data-availability={item.availability || 'UNKNOWN'}>
          {t(availabilityLabel(item.availability))}
        </span>
        {cart?.available ? (
          <button
            type="button"
            className="ae-card__add"
            aria-label={t('home.addToBasket')}
            disabled={outOfStock || state === 'busy' || !total}
            onClick={add}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M4 9h16" />
              <path d="M6 9l1.4 10.2a1 1 0 0 0 1 .8h7.2a1 1 0 0 0 1-.8L18 9" />
              <path d="M9 9l3-6 3 6" />
            </svg>
            {state === 'busy' ? t('home.adding') : state === 'added' ? t('home.addedShort') : t('home.add')}
          </button>
        ) : null}
        <span className="ae-add-status" role="status" aria-live="polite">
          {state === 'failed' ? t('home.addFailed') : state === 'added' ? <PortalLink href="/cart">{t('home.viewBasket')}</PortalLink> : null}
        </span>
      </div>
    </article>
  );
}
